/** Names the tour record knows by id beyond its own fields — the tag categories its rows
 *  name (stored rows, the operator's defaults, and categories picked for new rows), the
 *  pre-trip items of its rows, and the itinerary's activities, transport types and
 *  currencies (stored rows and picks) — so a
 *  ledger row reads "Tag category LANGUAGE · Check-in", not "Tag category #906". One per
 *  page, outside the draft (a pick is remembered for the session). */
import type { TourDetail } from '@/api/tours';

export interface CategoryName {
  id: number;
  name: string | null;
  code: string | null;
}

type Seed = Pick<TourDetail, 'tag_categories_own' | 'tag_categories_default'> &
  Partial<Pick<TourDetail, 'pre_trip_items' | 'itinerary'>>;

export class TourNames {
  readonly categories = new Map<number, CategoryName>();
  /** Pre-trip catalog items by id (stored rows, picks, imports from another tour). */
  readonly items = new Map<number, string | null>();
  /** The itinerary's catalog activities, transport types and currencies by id. */
  readonly activities = new Map<number, string | null>();
  readonly transports = new Map<number, string | null>();
  readonly currencies = new Map<number, string | null>();

  constructor(detail?: Seed) {
    if (detail) this.seed(detail);
  }

  seed(detail: Seed): void {
    for (const row of [...(detail.tag_categories_own ?? []), ...(detail.tag_categories_default ?? [])]) {
      if (row.category) this.rememberCategory(row.category);
    }
    for (const row of detail.pre_trip_items ?? []) if (row.item) this.rememberItem(row.item);
    for (const day of detail.itinerary?.days ?? []) {
      for (const slot of day.timeslots ?? []) {
        if (slot.next_transport) this.transports.set(slot.next_transport.id, slot.next_transport.name);
        for (const a of slot.activities ?? []) if (a.activity) this.activities.set(a.activity.id, a.activity.name);
      }
    }
  }

  activityLabel = (id: unknown): string => {
    if (typeof id !== 'number') return '—';
    return this.activities.get(id) || `Activity #${id}`;
  };

  transportLabel = (id: unknown): string => {
    if (typeof id !== 'number') return '—';
    return this.transports.get(id) || `Transport #${id}`;
  };

  currencyLabel = (id: unknown): string => {
    if (typeof id !== 'number') return '—';
    return this.currencies.get(id) || `Currency #${id}`;
  };

  rememberItem(item: { id: number; name: string | null }): void {
    this.items.set(item.id, item.name);
  }

  itemLabel(id: unknown): string {
    if (typeof id !== 'number') return '—';
    return this.items.get(id) || `Item #${id}`;
  }

  rememberCategory(c: CategoryName): void {
    this.categories.set(c.id, c);
  }

  categoryLabel(id: unknown): string {
    if (typeof id !== 'number') return '—';
    const c = this.categories.get(id);
    return c?.name || c?.code || `#${id}`;
  }
}
